import type { StyleRef } from '@wix/document-services-types';
import type { Style } from './types';

const pickPropsByKeyList = (
  props: Record<string, any> | undefined,
  keysList: Array<string>,
  createPrefixedPropertyName: (key: string) => string,
) => {
  if (!props) {
    return {};
  }
  return Object.fromEntries(
    keysList
      .map(key => [key, props[createPrefixedPropertyName(key)]])
      .filter(([_, value]) => value !== undefined),
  );
};
export const pickStyleProperties = (
  style: Style,
  propertiesList: Array<string>,
  createPrefixedPropertyName: (key: string) => string,
) => {
  const properties = pickPropsByKeyList(
    style.properties,
    propertiesList,
    createPrefixedPropertyName,
  );
  const propertiesOverride = pickPropsByKeyList(
    style.propertiesOverride,
    propertiesList,
    createPrefixedPropertyName,
  );
  const propertiesSource = pickPropsByKeyList(
    style.propertiesSource,
    propertiesList,
    createPrefixedPropertyName,
  );
  return {
    properties,
    propertiesOverride,
    propertiesSource,
  };
};

// FYI: This function removes properties from detached component that are not present in the top menu style
// Cases when style in detached component is missing in top menu style: migration, new style property that is absent in the current preset.
export const filterMissingProperties = (
  topMenuStyleProperties: Style['properties'],
  componentStyle: StyleRef,
  prefix: string,
) => {
  const shouldIncludeKey = (key: string): boolean => {
    return (
      key.includes('hover') ||
      key.includes('selected') ||
      topMenuStyleProperties.hasOwnProperty(`${prefix}-${key}`)
    );
  };

  const filterStyleProperties = (
    properties: Record<string, unknown> | undefined = {},
  ): Record<string, unknown> => {
    return Object.fromEntries(
      Object.entries(properties).filter(([key]) => shouldIncludeKey(key)),
    );
  };

  const filteredProperties = {
    properties: filterStyleProperties(componentStyle.style.properties),
    propertiesOverride: filterStyleProperties(
      componentStyle.style.propertiesOverride,
    ),
    propertiesSource: filterStyleProperties(
      componentStyle.style.propertiesSource,
    ),
  };

  return filteredProperties;
};

export const hasDetachedStyleProperties = (properties: Style['properties']) => {
  return properties.isDetached !== 'false';
};
